import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, useParams } from "react-router-dom"
import ContainerPage from "../../../components/ContainerPage/ContainerPage"
import Dropdown from "../../../components/Dropdown/Dropdown"
import { CrossCloseIcon } from "../../../components/Icons/Icons"
import { useMyContextLoading } from "../../../components/LoadingContextProvider/LoadingContextProvider"
import { showSnackBar } from "../../../components/Snackbar/Snackbar"
import { normalizeError } from "../../../utils/errorHelpers"
import ExternalApiConfig from "./ExternalApiConfig"
import TaskActionConfig from "./TaskActionConfig"
import {
  loadConditions,
  loadActions,
  loadTriggers,
  createTrigger,
  updateTrigger,
  selectConditions,
  selectActions,
  selectTriggers,
  selectError
} from "../../../features/workflow/workflowSlice"

const TriggerForm = ({ mode = "create" }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { id } = useParams()
  const { triggerLoading } = useMyContextLoading()

  const conditions = useSelector(selectConditions)
  const allActions = useSelector(selectActions)
  const triggers = useSelector(selectTriggers)
  const error = useSelector(selectError)

  const [formData, setFormData] = useState({
    name: "",
    conditionId: "",
    selectedActions: []
  })

  const [validationError, setValidationError] = useState("")
  const [currentAction, setCurrentAction] = useState("")
  const [actionConfigs, setActionConfigs] = useState({})
  const [initialActionConfigs, setInitialActionConfigs] = useState({})
  const [initialFormData, setInitialFormData] = useState({
    name: "",
    conditionId: "",
    selectedActions: []
  })

  useEffect(() => {
    const loadData = async () => {
      triggerLoading(true)
      try {
        await dispatch(loadConditions())
        await dispatch(loadActions())
        await dispatch(loadTriggers())
      } catch (error) {
        showSnackBar("error", normalizeError(error))
      }
      triggerLoading(false)
    }
    loadData()
  }, [dispatch])

  useEffect(() => {
    if (mode === "edit" && id && triggers.length > 0) {
      const trigger = triggers.find(t => t._id === id)
      if (trigger) {
        const initialData = {
          name: trigger.name,
          conditionId: trigger.condition?._id || trigger.condition,
          selectedActions: trigger.action?.map(a => a._id || a) || []
        }
        setFormData(initialData)
        setInitialFormData(initialData)

        if (trigger.actionConfigs && typeof trigger.actionConfigs === 'object') {
          setActionConfigs({ ...trigger.actionConfigs })
          setInitialActionConfigs({ ...trigger.actionConfigs })
        }
      }
    }
  }, [mode, id, triggers])

  const handleConditionChange = (conditionId) => {
    const condition = conditions.find(c => c._id === conditionId)
    setFormData(prev => ({
      ...prev,
      conditionId,
      name: condition?.name || ""
    }))
    setValidationError("")
  }

  const handleAddAction = (actionId) => {
    if (actionId && !formData.selectedActions.includes(actionId)) {
      setFormData(prev => ({
        ...prev,
        selectedActions: [...prev.selectedActions, actionId]
      }))
      setCurrentAction("")
      setValidationError("")
    }
  }

  const handleRemoveAction = (actionId) => {
    setFormData(prev => ({
      ...prev,
      selectedActions: prev.selectedActions.filter(id => id !== actionId)
    }))
    setActionConfigs(prev => {
      const newConfigs = { ...prev }
      delete newConfigs[actionId]
      return newConfigs
    })
  }

  const getActionName = (actionId) => {
    const action = allActions.find(a => a._id === actionId)
    return action ? action.name : actionId
  }

  const getActionType = (actionId) => {
    const action = allActions.find(a => a._id === actionId)
    return action?.type
  }

  const getActionKey = (actionId) => {
    const action = allActions.find(a => a._id === actionId)
    return action?.key
  }

  const handleActionConfigChange = (actionId, config) => {
    setActionConfigs(prev => ({
      ...prev,
      [actionId]: config
    }))
    setValidationError("")
  }

  const hasChanges = () => {
    if (mode === "create") return true

    // Check if actions changed
    const actionsChanged = formData.selectedActions.length !== initialFormData.selectedActions.length ||
      formData.selectedActions.some(id => !initialFormData.selectedActions.includes(id))

    // Check if action configs changed
    const configsChanged = JSON.stringify(actionConfigs) !== JSON.stringify(initialActionConfigs)

    return actionsChanged || configsChanged
  }

  const validateForm = () => {
    if (!formData.conditionId) {
      setValidationError("Trigger Type is required")
      return false
    }

    if (formData.selectedActions.length === 0) {
      setValidationError("At least one action is required")
      return false
    }

    for (const actionId of formData.selectedActions) {
      const actionType = getActionType(actionId)
      if (actionType === 'EXTERNAL_API') {
        const config = actionConfigs[actionId]
        const hasEndpoints = Array.isArray(config?.endpoints) && config.endpoints.length > 0 &&
          config.endpoints.every(endpoint => endpoint && endpoint.webhookUrl && endpoint.apiKey)
        const hasLegacyTarget = config?.webhookUrl && config?.apiKey
        const hasFields = config?.selectedFields && config.selectedFields.length > 0
        if (!config || !hasFields || (!hasEndpoints && !hasLegacyTarget)) {
          setValidationError("External API actions must be fully configured")
          return false
        }
      }
    }

    return true
  }

  const handleSubmit = async (e) => {
    if (e?.preventDefault) {
      e.preventDefault()
    }

    if (!validateForm()) return

    const actionsWithConfigs = formData.selectedActions.map(actionId => {
      const config = actionConfigs[actionId]
      if (config) {
        return { actionId, config }
      }
      return { actionId }
    })

    const submitData = {
      name: formData.name,
      conditionId: formData.conditionId,
      actionIds: formData.selectedActions,
      actionsConfig: actionsWithConfigs
    }

    triggerLoading(true)
    try {
      if (mode === "create") {
        await dispatch(createTrigger(submitData)).unwrap()
        showSnackBar("success", "Trigger created successfully")
      } else {
        await dispatch(updateTrigger({
          id,
          actionIds: formData.selectedActions,
          actionsConfig: actionsWithConfigs
        })).unwrap()
        showSnackBar("success", "Trigger updated successfully")
      }
      navigate("/settings", { state: { activeTab: 'workflowControl' } })
    } catch (error) {
      const errorMsg = normalizeError(error) || "Failed to save trigger"
      setValidationError(errorMsg)
      showSnackBar("error", errorMsg)
    }
    triggerLoading(false)
  }

  const handleCancel = () => {
    navigate("/settings", { state: { activeTab: 'workflowControl' } })
  }

  const availableActions = (allActions || [])
    .filter(a => !formData.selectedActions.includes(a._id))
    .map(a => ({ value: a._id, label: a.name }))

  const usedConditionIds = triggers.map(t => t.condition?._id || t.condition)
  const conditionsMenuItems = (conditions || [])
    .filter(c => mode === "edit" ? true : !usedConditionIds.includes(c._id))
    .map(c => ({ value: c._id, label: c.name }))

  return (
    <ContainerPage>
      <div className="flex px-6 w-[79%] flex-col gap-6 mb-[25px]">
        <h1 className="font-bold text-xl mb-2">
          {mode === "create" ? "Add Trigger" : "Edit Trigger"}
        </h1>

        <div className="mx-[95px] mt-10 px-6 py-5 rounded-2xl shadow-lg bg-surface border border-divider">
          <form onSubmit={handleSubmit} className="flex flex-col gap-6">
            {validationError && (
              <div className="p-3 bg-error-light border border-error-main text-error-main rounded text-sm">
                {normalizeError(validationError)}
              </div>
            )}

            {error && (
              <div className="p-3 bg-error-light border border-error-main text-error-main rounded text-sm">
                {normalizeError(error)}
              </div>
            )}

            <div>
              <Dropdown
                value={formData.conditionId}
                setValue={handleConditionChange}
                menuItems={conditionsMenuItems}
                className={mode === "edit" ? "opacity-60 pointer-events-none" : ""}
                label="Trigger Type"
                placeholder="Select trigger type..."
              />
            </div>

            <div>
              <Dropdown
                value={currentAction}
                setValue={handleAddAction}
                menuItems={availableActions}
                className=""
                label="Actions to Run"
                placeholder="Select action..."
              />

              {formData.selectedActions.length > 0 && (
                <div className="w-full flex flex-col text-md mt-4">
                  {formData.selectedActions.map((actionId, index) => (
                    <div key={actionId} className="mb-4">
                      <div className="flex flex-row items-center justify-between whitespace-nowrap mb-2">
                        <div className="flex items-center">
                          <span className="text-text-secondary mr-2">{index + 1}.</span>
                          <span>{getActionName(actionId)}</span>
                        </div>
                        <button
                          type="button"
                          className="ml-2"
                          onClick={() => handleRemoveAction(actionId)}
                        >
                          <CrossCloseIcon fill="var(--color-gray-600)" />
                        </button>
                      </div>

                      {getActionType(actionId) === 'EXTERNAL_API' && (
                        <ExternalApiConfig
                          config={actionConfigs[actionId] || {}}
                          onChange={(config) => handleActionConfigChange(actionId, config)}
                          actionId={actionId}
                        />
                      )}

                      {getActionType(actionId) === 'TASK' && (
                        <TaskActionConfig
                          config={actionConfigs[actionId] || {}}
                          onChange={(config) => handleActionConfigChange(actionId, config)}
                          actionKey={getActionKey(actionId)}
                        />
                      )}

                      {getActionKey(actionId) === 'ASSIGN_CONTACT' && (
                        <TaskActionConfig
                          config={actionConfigs[actionId] || {}}
                          onChange={(config) => handleActionConfigChange(actionId, config)}
                          actionKey={getActionKey(actionId)}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-divider">
              <button
                type="button"
                onClick={handleCancel}
                className="text-md px-4 py-[9px] max-w-max rounded-lg font-semibold text-text-primary border border-divider enabled:hover:bg-primary-main/10 enabled:hover:border-primary-main"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!hasChanges()}
                className="text-md px-4 py-[9px] max-w-max rounded-lg font-semibold bg-primary-main text-primary-contrast enabled:hover:bg-primary-main/90 shadow disabled:bg-surface disabled:cursor-not-allowed disabled:text-text-secondary"
              >
                Save
              </button>
            </div>
          </form>
        </div>
      </div>
    </ContainerPage>
  )
}

export default TriggerForm
